/**
 * Image ↔ instance-type compatibility for AWS, from what AWS reports on
 * both sides. Line-for-line twin of the helpers in api/util/awsCatalog.js
 * so the form greys out exactly what the deploy preflight would refuse.
 * Keep the two in sync.
 */

// Packer stamps every Heidi image with a numeric timestamp, which is what
// separates our images from anything else that happens to contain "-heidi-".
const IMAGE_NAME_RE = /^(.+)-(heidi|server|node|gpu)-(\d+)$/;

/** { prefix, kind, stamp } for a Heidi-built image name, or null. */
export function parseImageName(name) {
    const m = IMAGE_NAME_RE.exec(name || '');
    if (!m) return null;
    return { prefix: m[1], kind: m[2], stamp: m[3] };
}

/** AWS leaves BootMode unset on most images: x86 defaults to legacy BIOS, arm64 to UEFI. */
export function effectiveBootMode(image) {
    if (image.bootMode) return image.bootMode;
    return image.arch === 'arm64' ? 'uefi' : 'legacy-bios';
}

/**
 * The image terraform would pick for an image name: `<name>-heidi-*`
 * (unified), `<name>-server*` (legacy head), `<prefix>-node-*` / `-gpu-*`
 * (legacy nodes), newest wins. The kind boundary keeps `u26` from matching
 * `u26aws-heidi-…`.
 */
export function resolveStackImage(images, imageName, kind = 'heidi') {
    if (!imageName || !Array.isArray(images)) return null;
    const stem = kind === 'server' ? `${imageName}-server` : `${imageName}-${kind}-`;
    const matches = images.filter((img) => img.kind === kind && img.name.startsWith(stem));
    if (matches.length === 0) return null;
    return matches.reduce((best, img) => (img.createdAt > best.createdAt ? img : best));
}

/** [{ prefix, kinds, newestByKind, count }] sorted by prefix. */
export function groupImagesByPrefix(images) {
    const byPrefix = {};
    (images || []).forEach((img) => {
        if (!img.kind) return;
        const g = byPrefix[img.prefix] || (byPrefix[img.prefix] = { prefix: img.prefix, kinds: [], newestByKind: {}, count: 0 });
        g.count += 1;
        if (!g.kinds.includes(img.kind)) g.kinds.push(img.kind);
        if (!g.newestByKind[img.kind] || img.createdAt > g.newestByKind[img.kind].createdAt) g.newestByKind[img.kind] = img;
    });
    return Object.values(byPrefix).sort((a, b) => a.prefix.localeCompare(b.prefix));
}

const archLabel = (arch) => (arch === 'x86_64' ? 'x86' : arch);

/**
 * Can this image boot on this instance type? `shape` is a selector shape
 * from useAwsCatalog (archs, virtTypes, bootModes, rootDeviceTypes,
 * enaSupport). Returns { compatible: true } or
 * { compatible: false, code, shapeReason, imageReason }.
 */
export function imageShapeCompatibility(image, shape, options) {
    if (!image || !shape) return { compatible: true };
    const id = shape.instance_id;
    // Marketplace products license a fixed list of instance types (the rate
    // card's dimensions); AWS refuses anything else at RunInstances.
    const licensed = options && options.licensedInstanceTypes;
    const product = (options && options.productName) || 'this Marketplace product';
    if (Array.isArray(licensed) && licensed.length && !licensed.includes(id)) {
        return {
            compatible: false, code: 'LICENSE',
            shapeReason: `not licensed for ${product}`,
            imageReason: `${product} licence does not cover ${id}`,
        };
    }
    const shapeArch = shape.architecture || shape.arch;
    const archs = shape.archs && shape.archs.length ? shape.archs : [shapeArch];
    if (!archs.includes(image.arch)) {
        return {
            compatible: false, code: 'ARCH',
            shapeReason: `${archLabel(shapeArch)} shape, image is ${archLabel(image.arch)}`,
            imageReason: `${archLabel(image.arch)} image, ${id} is ${archLabel(shapeArch)}`,
        };
    }
    const virtTypes = shape.virtTypes || [];
    if (virtTypes.length && !virtTypes.includes(image.virt)) {
        return {
            compatible: false, code: 'VIRT',
            shapeReason: `needs ${virtTypes.join('/')}, image is ${image.virt}`,
            imageReason: `${image.virt} image, ${id} needs ${virtTypes.join('/')}`,
        };
    }
    const rootTypes = shape.rootDeviceTypes || [];
    if (rootTypes.length && !rootTypes.includes(image.rootDeviceType)) {
        return {
            compatible: false, code: 'ROOT',
            shapeReason: `needs ${rootTypes.join('/')} root, image is ${image.rootDeviceType}`,
            imageReason: `${image.rootDeviceType}-root image, ${id} needs ${rootTypes.join('/')}`,
        };
    }
    if (shape.enaSupport === 'required' && !image.ena) {
        return {
            compatible: false, code: 'ENA',
            shapeReason: 'needs ENA, image lacks it',
            imageReason: `no ENA, ${id} needs it`,
        };
    }
    const bootModes = shape.bootModes || [];
    const boot = effectiveBootMode(image);
    if (bootModes.length && boot !== 'uefi-preferred' && !bootModes.includes(boot)) {
        return {
            compatible: false, code: 'BOOT',
            shapeReason: `needs ${bootModes.join('/')} boot, image is ${boot}`,
            imageReason: `${boot} image, ${id} needs ${bootModes.join('/')}`,
        };
    }
    return { compatible: true };
}

/** Short human line for an image: "u26aws-heidi-1785601158 · arm64 · hvm/uefi · built 2026-08-01". */
export function describeImage(image) {
    if (!image) return '';
    const built = image.createdAt ? ` · built ${String(image.createdAt).slice(0, 10)}` : '';
    return `${image.name} · ${image.arch} · ${image.virt}/${effectiveBootMode(image)}${built}`;
}

/** Instance + software per hour; null when either part is unknown but expected. */
export function totalHourly(instancePrice, softwareRate) {
    if (instancePrice == null || !isFinite(instancePrice)) return null;
    if (softwareRate == null) return instancePrice;
    return Math.round((instancePrice + softwareRate) * 10000) / 10000;
}

/**
 * The software-pricing entry (from /api/aws/software-pricing) that applies to
 * an image, or null for a plain image. An image with several product codes
 * (unusual) reports the first priced one.
 */
export function softwareEntryFor(image, softwarePricing) {
    if (!image || !image.productCodes || image.productCodes.length === 0) return null;
    const byCode = (softwarePricing && softwarePricing.byProductCode) || {};
    for (const code of image.productCodes) {
        if (byCode[code]) return byCode[code];
    }
    return { productCode: image.productCodes[0], productId: null, subscribed: null, rates: {}, licensedInstanceTypes: [], flatRate: null, minRate: null, maxRate: null, freeTrial: null, unknown: true };
}

/** Software $/hr for a shape under an entry, or null when unknown. */
export function softwareRateFor(entry, instanceId) {
    if (!entry || !entry.rates) return null;
    const usd = entry.rates[instanceId];
    return typeof usd === 'number' && isFinite(usd) ? usd : null;
}
